import React, { useState, useEffect, useCallback } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useAuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lightbulb, Send, Loader2, ChevronDown, ChevronUp, MessageSquare } from "lucide-react";

const CATEGORY_KEYS = [
  { value: "hiring", labelKey: "sug_cat_hiring", emoji: "💼" },
  { value: "buy_sell", labelKey: "sug_cat_buy_sell", emoji: "🛍️" },
  { value: "events", labelKey: "sug_cat_events", emoji: "🎉" },
  { value: "fundraisers", labelKey: "sug_cat_fundraisers", emoji: "❤️" },
  { value: "other", labelKey: "sug_cat_other", emoji: "💡" },
];

const STATUS_STYLES = {
  new: { bg: "var(--sand-2)", color: "var(--ink-2)" },
  planned: { bg: "#FFE9A8", color: "#9A6700" },
  in_progress: { bg: "#CDE7FF", color: "#0F4D8C" },
  done: { bg: "#D7F2DA", color: "#1E7B36" },
  rejected: { bg: "#F5D5D2", color: "#8C2017" },
};

export default function SuggestionsSection() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { open: openAuth } = useAuthDialog();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("hiring");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const loadMine = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await http.get("/suggestions/mine");
      queueMicrotask(() => setMine(data));
    } catch {
      /* silent */
    }
  }, [user?.id]);

  useEffect(() => { loadMine(); }, [loadMine]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      openAuth("login");
      return;
    }
    if (title.trim().length < 3 || body.trim().length < 10) {
      toast(t("sug_validation"));
      return;
    }
    queueMicrotask(() => setBusy(true));
    try {
      await http.post("/suggestions", { title: title.trim(), body: body.trim(), category });
      toast.success(t("sug_thanks"));
      queueMicrotask(() => { setTitle(""); setBody(""); setCategory("hiring"); });
      loadMine();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not submit");
    } finally {
      queueMicrotask(() => setBusy(false));
    }
  };

  return (
    <section id="suggestions" data-testid="suggestions-section" className="clay p-6 md:p-7 scroll-mt-24">
      <div className="flex items-start gap-3 mb-2">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--coral)" }}>
          <Lightbulb className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="eyebrow">{t("sug_eyebrow")}</p>
          <h2 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
            {t("sug_title")}
          </h2>
          <p className="text-sm text-[color:var(--ink-2)] mt-1 max-w-2xl">
            {t("sug_subtitle")}
          </p>
        </div>
      </div>

      <div className="grid gap-2 mt-4 mb-4 grid-cols-2 sm:grid-cols-5">
        {CATEGORY_KEYS.map((c) => (
          <div
            key={c.value}
            className="rounded-2xl bg-[color:var(--sand-2)] px-3 py-2.5 text-center text-xs font-medium text-[color:var(--ink-2)]"
            data-testid={`sug-cat-chip-${c.value}`}
          >
            <span className="text-lg block mb-0.5" aria-hidden>{c.emoji}</span>
            {t(c.labelKey)}
          </div>
        ))}
      </div>

      {user?.id ? (
        <form onSubmit={submit} className="space-y-2" data-testid="suggestion-form">
          <div className="grid sm:grid-cols-[1fr_200px] gap-2">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("sug_title_placeholder")}
              maxLength={100}
              className="rounded-2xl"
              data-testid="sug-title"
              required
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-2xl border border-[color:var(--ink-2)]/20 px-3 text-sm bg-white"
              data-testid="sug-category"
            >
              {CATEGORY_KEYS.map((c) => (
                <option key={c.value} value={c.value}>{t(c.labelKey)}</option>
              ))}
            </select>
          </div>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("sug_body_placeholder")}
            rows={3}
            maxLength={2000}
            className="rounded-2xl"
            data-testid="sug-body"
            required
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[11px] text-[color:var(--ink-2)]">
              {t("sug_signed_as")} <strong>{user.name || user.username}</strong>
            </span>
            <Button
              type="submit"
              disabled={busy}
              data-testid="sug-submit"
              className="pill bg-[color:var(--coral)] text-white hover:opacity-90"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              {t("sug_submit")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="rounded-2xl bg-[color:var(--sand-2)] p-4 text-sm text-[color:var(--ink-2)] flex items-center justify-between gap-3 flex-wrap" data-testid="sug-login-prompt">
          <span>{t("sug_login_prompt")}</span>
          <Button onClick={() => openAuth("login")} className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]">
            {t("login")}
          </Button>
        </div>
      )}

      {user?.id && mine.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[color:var(--ink-2)]/10">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center justify-between w-full text-left"
            data-testid="sug-mine-toggle"
          >
            <span className="eyebrow flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" />
              {t("sug_mine_title")} ({mine.length})
            </span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expanded && (
            <ul className="mt-3 space-y-2" data-testid="sug-mine-list">
              {mine.map((s) => {
                const style = STATUS_STYLES[s.status] || STATUS_STYLES.new;
                return (
                  <li key={s.id} className="rounded-2xl border border-[color:var(--ink-2)]/10 p-3 bg-white">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{s.title}</span>
                      <span
                        className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                        style={{ background: style.bg, color: style.color }}
                      >
                        {t(`sug_status_${s.status}`)}
                      </span>
                    </div>
                    <p className="text-xs text-[color:var(--ink-2)] mt-1 line-clamp-3">{s.body}</p>
                    {s.admin_note && (
                      <div className="mt-2 text-xs bg-[color:var(--sand-2)] rounded-xl p-2 border-l-2 border-[color:var(--coral)]">
                        <strong className="text-[color:var(--coral)]">{t("sug_admin_reply")}: </strong>
                        {s.admin_note}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
